import { Injectable, signal } from '@angular/core';
import { ExportData } from './models/export-data.modal';
import { ExportDataService } from './export-data.service';
import { NotifyService } from '@services/core-services/notify.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Query } from './models/query';
import { PaginatedList } from './models/paginated-list';

interface LegacyResponse {
  exports?: ExportData[];
}

@Injectable({
  providedIn: 'root',
})
export class DownloadsService {
  private downloads = signal<ExportData[]>([]);
  private errorMessage = signal<string | null>(null);
  private currentPage = 1;
  private hasMorePages = true;
  private totalRecords = signal<number>(0);

  constructor(
    private exportService: ExportDataService,
    private notify: NotifyService,
    private errorHandler: ErrorHandlerService
  ) {
    this.loadInitialDownloads();
  }

  loadInitialDownloads() {
    const query: Query = {
      page: 1,
      limit: 25,
    };

    this.exportService.downloadDataList(query).subscribe({
      next: (response: PaginatedList<ExportData[]> | LegacyResponse) => {
        if ('list' in response) {
          this.downloads.set(response.list);
          this.currentPage = 1;
          this.hasMorePages = !!response.pagination?.next_page;
          this.totalRecords.set(response.pagination?.total_records || 0);
          this.errorMessage.set(null);
        } else if (response?.exports) {
          // Handle legacy response format
          this.downloads.set(response.exports);
          this.hasMorePages = false;
          this.totalRecords.set(response.exports.length);
          this.errorMessage.set(null);
        } else {
          this.downloads.set([]);
          this.totalRecords.set(0);
          this.errorMessage.set(null);
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading downloads:', error);
        const errorResponse = this.errorHandler.handleServerError(error);
        this.errorMessage.set(errorResponse.message);
        this.downloads.set([]);
        this.totalRecords.set(0);
        this.hasMorePages = false;
      },
    });
  }

  addDownloadRequest(request: ExportData) {
    this.downloads.update(downloads => [...downloads, request]);
    this.totalRecords.update(count => count + 1);
    this.notify.success(
      'Export request initiated. You can download the file from the downloads list when ready.'
    );
  }

  updateDownloadStatus(id: string, error?: string) {
    this.downloads.update(downloads =>
      downloads.map(download =>
        download.job_id === id ? { ...download, error_message: error || null } : download
      )
    );
  }

  getDownloadRequests() {
    return this.downloads;
  }

  getErrorMessage() {
    return this.errorMessage;
  }

  downloadFile(id: string): void {
    const download = this.downloads().find(d => d.job_id === id);
    if (!download?.download?.url) {
      const errorMsg = 'Download URL not available';
      this.errorMessage.set(errorMsg);
      this.notify.error(errorMsg);
      return;
    }

    // Open URL in new tab
    window.open(download.download.url);
  }

  downloadsCount() {
    return this.totalRecords();
  }

  hasMore() {
    return this.hasMorePages;
  }

  getCurrentPage() {
    return this.currentPage;
  }
}
